import { useEffect, useMemo, useState } from 'react';
import {
    useDataTable,
    useDataTableExpansion,
    type DataTableExpansion,
    type DataTableFilter,
    type DataTableMode,
    type DataTableRefs,
    type MoreMenuCategory,
    type SortOrder
} from '@odo/shared-ui';
import {
    useBillingVendors,
    useInfinitePurchaseOrders,
    usePurchaseOrders
} from '../../../hooks/usePurchaseOrders';
import { usePracticeOptions } from '../../../hooks/useBilling';
import { DATA_TABLE_TALL_ROW_HEIGHT } from '../../../hooks/useViewportPageSize';
import { useViewportTableLimit } from '../../../hooks/useViewportTableLimit';
import type {
    PurchaseOrderListItem,
    PurchaseOrderStageSummary,
    PurchaseOrdersSortBy
} from '../../../services/purchaseOrders';
import { buildPurchaseOrderFilters, buildPurchaseOrderMoreFilters } from './purchaseOrderFilters';
import type { PurchaseOrderStageFilters } from './poStageFilters';

const SEARCH_DEBOUNCE_MS = 300;
export const FILL_HEIGHT_PAGE_SIZE = 15;
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

// A type alias, not an interface: only aliases carry the implicit index
// signature `DataTableFilterMap` needs.
type PurchaseOrderFilters = {
    vendorId: string[];
    fulfillmentStatus: string[];
    billingStatus: string[];
    needsAttention: boolean;
    // Single-select: the endpoint takes one practiceId, so the pill must not
    // offer a multi-selection it would silently drop.
    practiceId: string | null;
};

export type PurchaseOrderFilterName = keyof PurchaseOrderFilters;

function withoutPracticeKey(
    keys: Readonly<Record<PurchaseOrderFilterName, string>>
): Readonly<Record<string, string>> {
    const { practiceId: _practiceId, ...rest } = keys;
    return rest;
}

// Soonest due first, newest created first. Matches the endpoint's default.
const defaultOrderFor = (by: PurchaseOrdersSortBy) =>
    by === 'vendorInvoiceDueDate' ? ('asc' as const) : ('desc' as const);

export interface UsePurchaseOrdersListStateArgs {
    practiceId?: string;
    chargeId?: string;
    filterStorageKeys: Readonly<Record<PurchaseOrderFilterName, string>>;
    fillHeight: boolean;
    onTotalCountChange?: (count: number) => void;
}

export interface PurchaseOrdersListState {
    rows: PurchaseOrderListItem[];
    total: number;
    totalPages: number;
    page: number;
    isLoading: boolean;
    isFetching: boolean;
    mode: DataTableMode;
    setMode: (mode: DataTableMode) => void;
    setPage: (page: number) => void;
    sort: {
        by: PurchaseOrdersSortBy;
        order: SortOrder;
        onSort: (by: PurchaseOrdersSortBy) => void;
    };
    searchInput: string;
    setSearchInput: (value: string) => void;
    filters: DataTableFilter[];
    /** Shipping and Billing - tucked behind the toolbar's "More filters" menu. */
    moreFilters: MoreMenuCategory[];
    moreFiltersActiveCount: number;
    loadMore: { fetchNextPage: () => void; hasMore: boolean; isLoadingMore: boolean };
    refs: DataTableRefs | undefined;
    expansion: DataTableExpansion;
    showPracticeColumn: boolean;
    stageSummary: PurchaseOrderStageSummary;
    stageFilters: PurchaseOrderStageFilters;
    setStageFilters: (filters: PurchaseOrderStageFilters) => void;
}

// Everything the purchase-orders table needs from the data layer, so the
// content component is left with rendering and drawer hosting only.
export function usePurchaseOrdersListState({
    practiceId,
    chargeId,
    filterStorageKeys,
    fillHeight,
    onTotalCountChange
}: UsePurchaseOrdersListStateArgs): PurchaseOrdersListState {
    const table = useDataTable<PurchaseOrdersSortBy, PurchaseOrderFilters>({
        defaultSort: { by: 'createdAt', order: 'desc' },
        defaultOrderFor,
        defaultLimit: fillHeight ? FILL_HEIGHT_PAGE_SIZE : VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: {
            vendorId: [],
            fulfillmentStatus: [],
            billingStatus: [],
            needsAttention: false,
            practiceId: null
        },
        // A practice-scoped instance hides the pill, so it stops persisting one.
        filterStorageKeys: practiceId ? withoutPracticeKey(filterStorageKeys) : filterStorageKeys
    });
    const {
        vendorId,
        fulfillmentStatus,
        billingStatus,
        needsAttention,
        practiceId: practiceFilter
    } = table.filters;
    const isInfinite = table.mode === 'infinite';

    const expansion = useDataTableExpansion();

    const [searchInput, setSearchInput] = useState('');
    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput, commitSearch]);

    const viewport = useViewportTableLimit(table, {
        rowHeight: DATA_TABLE_TALL_ROW_HEIGHT,
        fixedPageSize: fillHeight ? FILL_HEIGHT_PAGE_SIZE : undefined
    });

    const { page, limit, sort, order } = table.params;
    const sharedParams = {
        limit,
        q: table.search || undefined,
        chargeId,
        // The prop wins: a scoped instance can't be widened by a stored filter.
        practiceId: practiceId ?? practiceFilter ?? undefined,
        vendorId,
        fulfillmentStatus,
        billingStatus,
        needsAttention: needsAttention || undefined,
        sort,
        order
    };
    // Both hooks are called unconditionally (rules of hooks) and only one is
    // ever enabled - same paged/infinite split as InvoicesContent.
    const paged = usePurchaseOrders({
        ...sharedParams,
        page,
        enabled: !isInfinite && viewport.isReady
    });
    const infinite = useInfinitePurchaseOrders({ ...sharedParams, enabled: isInfinite });

    const rows = isInfinite ? infinite.purchaseOrders : paged.purchaseOrders;
    const total = isInfinite ? infinite.total : paged.total;
    const stageSummary = isInfinite ? infinite.stageSummary : paged.stageSummary;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;
    const isFetching = isInfinite ? infinite.isFetching : paged.isFetching;

    useEffect(() => {
        onTotalCountChange?.(total);
    }, [total, onTotalCountChange]);

    const { vendors } = useBillingVendors();
    const vendorOptions = useMemo(
        () => vendors.map((vendor) => ({ value: vendor.id, label: vendor.name })),
        [vendors]
    );

    // Skipped on a scoped instance: redundant pill, pointless request.
    const { practices } = usePracticeOptions(!practiceId);
    const practiceOptions = useMemo(
        () =>
            practices.map((p) =>
                p.doctorName
                    ? {
                          value: p.id,
                          label: p.name,
                          sublabel: `Dr. ${p.doctorName}`,
                          keywords: [`Dr. ${p.doctorName}`, p.doctorName]
                      }
                    : { value: p.id, label: p.name }
            ),
        [practices]
    );

    const filters = buildPurchaseOrderFilters({
        vendorId,
        practiceFilter,
        vendorOptions,
        practiceOptions,
        practiceScoped: Boolean(practiceId),
        setVendor: (value) => table.setFilter('vendorId', value),
        setPractice: (value) => table.setFilter('practiceId', value)
    });

    const moreFilters = buildPurchaseOrderMoreFilters({
        fulfillmentStatus,
        billingStatus,
        setFulfillmentStatus: (value) => table.setFilter('fulfillmentStatus', value),
        setBillingStatus: (value) => table.setFilter('billingStatus', value)
    });
    const moreFiltersActiveCount = moreFilters.filter((f) => f.selectedOptions.length > 0).length;

    // The stage cards and the Shipping/Billing dropdowns write the same three
    // fields, so this is the one place a card click turns into filter state.
    const setStageFilters = (next: PurchaseOrderStageFilters) => {
        table.setFilter('fulfillmentStatus', next.fulfillmentStatus);
        table.setFilter('billingStatus', next.billingStatus);
        table.setFilter('needsAttention', next.needsAttention);
    };

    const refs: DataTableRefs | undefined = fillHeight
        ? undefined
        : {
              card: viewport.tableCardRef,
              head: viewport.tableHeaderRef,
              body: viewport.tableBodyRef,
              pagination: viewport.paginationRef
          };

    return {
        rows,
        total,
        totalPages: Math.max(1, Math.ceil(total / limit)),
        page,
        isLoading,
        isFetching,
        mode: table.mode,
        setMode: table.setMode,
        setPage: table.setPage,
        sort: { by: table.sort, order: table.order, onSort: table.toggleSort },
        searchInput,
        setSearchInput,
        filters,
        moreFilters,
        moreFiltersActiveCount,
        loadMore: {
            fetchNextPage: infinite.fetchNextPage,
            hasMore: infinite.hasNextPage,
            isLoadingMore: infinite.isFetchingNextPage
        },
        refs,
        expansion,
        showPracticeColumn: !practiceId,
        stageSummary,
        stageFilters: { fulfillmentStatus, billingStatus, needsAttention },
        setStageFilters
    };
}
